import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { TranslateService } from "@ngx-translate/core";
import { catchError, Observable, throwError } from "rxjs";
import { BaseService } from "../base.service";
import { Page } from "../commons/page";
import { Transaction } from "./model/transaction.model";
import moment from 'moment';
import { RefundRequest } from "./model/refund-request.model";
import { RefundOrderRequest } from "./model/refund-order-request.model";
import { PaymentStatusEnum } from "./model/enum/payment-status.enum";
import { PaymentType } from "./model/enum/payment-type.enum";
import { NotificationService } from "../util/notification.service";

export interface RefundRequestWithOrder {

  comment?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TransactionService extends BaseService {
  private financialEngineUrl = environment.api.financialUrl;

  constructor(private httpClient: HttpClient, protected notificationService: NotificationService, protected translate: TranslateService) {
    super(notificationService, translate);
  }

  /* PAYMENTS */

  getTransactions(filters: any, mobilityAccountId: number = null, sortDirection: string = '', sortActive: string = '', page: number = 0, size: number = 10): Observable<Page<Transaction>> {
    let params = new HttpParams()
    .set('page', page.toString())
    .set('size', size.toString());

    if (sortActive && sortDirection) {
      params = params.set('sort', sortActive + ',' + sortDirection);
    }

    if (mobilityAccountId) {
      params = params.set('mobilityAccountId', mobilityAccountId);
    }
    if (filters?.reference) {
      params = params.set('reference', filters.reference);
    }
    if (filters?.types) {
      if (Array.isArray(filters.types)) {
        filters.types.forEach(type => {
          params = params.append('type', type)
        });
      } else {
        params = params.set('type', filters.types)
      }
    }
    if (filters?.beginDate) {
      params = params.set('fromCreationDate', moment(filters.beginDate).startOf('day').toISOString());
    }
    if (filters?.endDate) {
      params = params.set('toCreationDate', moment(filters.endDate).endOf('day').toISOString());
    }
    if (filters?.paymentMeanType) {
      params = params.set('paymentMeanType', filters.paymentMeanType);
    }
    if (filters?.paymentMeanId) {
      params = params.set('paymentMeanId', filters.paymentMeanId);
    }
    if (filters?.vendor) {
      params = params.set('vendor', filters.vendor);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.transactionId) {
      params = params.set('transactionId', filters.transactionId);
    }
    if (filters?.groupId) {
      params = params.set('groupId', filters.groupId);
    }

    return this.httpClient.get<Page<Transaction>>(`${this.financialEngineUrl}/api/payment-manager/v1/payments`, { params });
  }

  getTransactionById(paymentId: number): Observable<Transaction> {
    return this.httpClient.get<Transaction>(`${this.financialEngineUrl}/api/payment-manager/v1/payments/${paymentId}`);
  }

  updateTransactionById(paymentId: number, transaction: Transaction): Observable<Transaction> {
    return this.httpClient.put<Transaction>(`${this.financialEngineUrl}/api/payment-manager/v1/payments/${paymentId}`, transaction).pipe(
      catchError(_err => {
        if (_err.status === 403) {
          console.error(`The agent doesn't have the permission to update payments`);
        }
        console.error(_err);
        return throwError(() => _err);
      })
    );
  }

  updateTransactionExternalRegularizationById(paymentId: number, transaction: Transaction): Observable<Transaction> {
    return this.httpClient.put<Transaction>(`${this.financialEngineUrl}/api/payment-manager/v1/payments/${paymentId}/external-regularization`, transaction).pipe(
      catchError(_err => {
        if (_err.status === 403) {
          console.error(`The agent doesn't have the permission to update payments`);
        }
        console.error(_err);
        return throwError(() => _err);
      })
    );
  }

  /* REFUND */

  createRefund(paymentId: number, refundBody: RefundRequest) {
    return this.httpClient.post(`${this.financialEngineUrl}/api/payment-manager/v1/payments/${paymentId}/refund`, refundBody);
  }

  createRefundWithOrder(paymentId: number, refundOrderBody: RefundOrderRequest) {
    return this.httpClient.post(`${this.financialEngineUrl}/api/payment-manager/v1/payments/${paymentId}/refund-order`, refundOrderBody);
  }

  /* UTILS */

  isRefundable(transaction: Transaction) {
    if (transaction) {
      if (!transaction.amountRefunded) {
        transaction.amountRefunded = 0;
      }
      return transaction.status === PaymentStatusEnum.PAID && transaction.type !== PaymentType.REFUND && transaction.transactionId && transaction.amount - transaction.amountRefunded > 0;
    }
    return false;
  }

}
